import React, { ReactNode, useCallback, useMemo } from 'react';
import { Platform, StyleSheet, View, ViewStyle, useWindowDimensions } from 'react-native';
import { Route, SceneRendererProps, TabView } from 'react-native-tab-view';

import { OrderLocatorTab } from '@fhs-legacy/frontend/src/utils/launchdarkly/variations';

import { TabRestaurants } from '../../hooks/use-locator-restaurants';
import { LocatorTabs, getLocatorPanelId } from '../locator-tabs';
import { StoreList } from '../store-list';

const isWeb = Platform.OS === 'web';

const EMPTY_TAB: TabRestaurants = { restaurants: [], loading: false, hasError: false };

interface LocatorRoute extends Route {
  key: OrderLocatorTab;
}

interface StoreListPagerProps {
  tabs: OrderLocatorTab[];
  activeTab: OrderLocatorTab;
  onTabChange: (tab: OrderLocatorTab) => void;
  restaurantsByTab: Partial<Record<OrderLocatorTab, TabRestaurants>>;
  selectedRestaurantId: string | null;
  onSelectRestaurant: (restaurantId: string) => void;
  hasSearchedLocation: boolean;
  /** Base id the tab bar points `aria-controls` at; each scene derives its own from it. */
  panelId: string;
}

/**
 * Tab bar plus a swipeable pager over one store list per tab, so the list can be dragged
 * sideways to move between Nearby, Previous and Favorites instead of only tapping a tab.
 */
export const StoreListPager: React.VFC<StoreListPagerProps> = ({
  tabs,
  activeTab,
  onTabChange,
  restaurantsByTab,
  selectedRestaurantId,
  onSelectRestaurant,
  hasSearchedLocation,
  panelId,
}) => {
  const { width: windowWidth } = useWindowDimensions();

  const routes = useMemo<LocatorRoute[]>(() => tabs.map(tab => ({ key: tab })), [tabs]);
  // Guests only have Nearby, and a tab can disappear on sign-out while it is the active one.
  const index = Math.max(0, tabs.indexOf(activeTab));

  const onIndexChange = useCallback(
    (nextIndex: number) => onTabChange(tabs[nextIndex]),
    [onTabChange, tabs]
  );

  const renderTabBar = useCallback(
    ({ position }: SceneRendererProps) => (
      <LocatorTabs
        tabs={tabs}
        activeTab={activeTab}
        onChange={onTabChange}
        panelId={panelId}
        position={position}
      />
    ),
    [tabs, activeTab, onTabChange, panelId]
  );

  const renderScene = useCallback(
    ({ route }: SceneRendererProps & { route: LocatorRoute }) => {
      const { restaurants, loading, hasError } = restaurantsByTab[route.key] ?? EMPTY_TAB;

      return (
        <LocatorScene isActive={route.key === activeTab}>
          <StoreList
            restaurants={restaurants}
            tab={route.key}
            isLoading={loading}
            hasError={hasError}
            selectedRestaurantId={selectedRestaurantId}
            onSelectRestaurant={onSelectRestaurant}
            hasSearchedLocation={hasSearchedLocation}
            panelId={getLocatorPanelId(panelId, route.key)}
          />
        </LocatorScene>
      );
    },
    [
      restaurantsByTab,
      activeTab,
      selectedRestaurantId,
      onSelectRestaurant,
      hasSearchedLocation,
      panelId,
    ]
  );

  return (
    <TabView
      navigationState={{ index, routes }}
      onIndexChange={onIndexChange}
      renderScene={renderScene}
      renderTabBar={renderTabBar}
      // The neighbouring tab has to be mounted to slide into view mid-drag; tabs further out
      // stay unmounted until they are swiped near.
      lazy
      lazyPreloadDistance={1}
      // Only a first-paint estimate. The desktop panel is narrower than the window, and the
      // pager remeasures its own container on layout.
      initialLayout={{ width: windowWidth }}
      swipeEnabled={routes.length > 1}
      style={pagerContainerStyle}
    />
  );
};

interface LocatorSceneProps {
  isActive: boolean;
  children: ReactNode;
}

/**
 * Every mounted scene sits in the DOM (and the native view tree) at once, so the ones the guest
 * is not on have to be hidden from assistive tech. On web this also keeps the address-search
 * focus handoff honest: it skips targets inside `aria-hidden`/`inert` subtrees, so it lands on
 * the visible list rather than a store card parked one tab over.
 */
const LocatorScene: React.VFC<LocatorSceneProps> = ({ isActive, children }) => (
  <View
    collapsable={false}
    style={styles.scene}
    accessible={false}
    accessibilityElementsHidden={!isActive}
    importantForAccessibility={
      Platform.OS === 'android' ? (isActive ? 'auto' : 'no-hide-descendants') : undefined
    }
    {...(isWeb && !isActive ? inactiveSceneWebProps : {})}
  >
    {children}
  </View>
);

const inactiveSceneWebProps = {
  'aria-hidden': true,
  inert: true,
} as const;

const styles = StyleSheet.create({
  pager: {
    flex: 1,
  },
  scene: {
    flex: 1,
  },
});

/**
 * The swipe is a JS gesture the browser knows nothing about, so under the default `touch-action`
 * a horizontal drag starts a native pan as well. `body` is a scroll container on web — its
 * `overflow-y: auto` makes the browser compute `overflow-x` as `auto` too — so that pan
 * rubber-bands the whole page sideways alongside the swipe. Claiming the horizontal axis for the
 * pager leaves the vertical scroll of the store list alone.
 */
const pagerContainerStyle = isWeb
  ? [styles.pager, { touchAction: 'pan-y' } as unknown as ViewStyle]
  : styles.pager;
